import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { memo, ComponentType, FunctionComponent } from 'react';

/**
 * Packages & components
 */
import {
  Values,
  RedirectRule,
  SkillSetRoutes,
  SkillsetRouteRule,
  SkillSetRouteMapping,
  PageNamesWithEmptyPathParams,
  PageNamesWithNonEmptyPathParams,
  SingleRoutePathParametersObject,
  PageNamesWithNonEmptyQueryParams,
  SingleRouteQueryParametersObject
} from '@astrumu/route-mapping';
import { useRouteMapping } from '@astrumu/ui-library';

import { ProtectedRoute } from '../ProtectedRoute';

/**
 * Queries
 */
import { useMeQuery } from '@/graphql/generated';

interface Props {
  path: Values<SkillSetRoutes>['path'];
  component: ComponentType<any>;
}

function getPath(
  routeMapping: SkillSetRouteMapping,
  rule: RedirectRule<SkillsetRouteRule>
) {
  const { redirectTo, pathParams, queryParams } = rule;

  if (queryParams) {
    return routeMapping.getPathByPageName(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      redirectTo as PageNamesWithNonEmptyQueryParams<SkillSetRoutes>,
      queryParams as SingleRouteQueryParametersObject<
        SkillSetRoutes,
        PageNamesWithNonEmptyQueryParams<SkillSetRoutes>
      >
    );
  }

  if (pathParams) {
    return routeMapping.getPathByPageName(
      redirectTo as PageNamesWithNonEmptyPathParams<SkillSetRoutes>,
      pathParams as SingleRoutePathParametersObject<
        SkillSetRoutes,
        PageNamesWithNonEmptyPathParams<SkillSetRoutes>
      >
    );
  }

  return routeMapping.getPathByPageName(
    redirectTo as PageNamesWithEmptyPathParams<SkillSetRoutes>
  );
}

export const RegularRoute: FunctionComponent<Props> = memo(
  ({ path, component: Component }) => {
    const { isAuthenticated } = useAuth0();
    const { data: meData } = useMeQuery();

    const routeMapping = useRouteMapping('skillset');

    const redirectRules = routeMapping.getRedirectRulesByPageName(
      routeMapping.getPageNameByPath(path)
    );

    const rules = redirectRules(meData?.me);

    const filteredRules = rules.filter(
      rule => !rule.isRenderAllowed && 'redirectTo' in rule
    );

    if (
      !isAuthenticated &&
      rules.find(({ name }) => name === SkillsetRouteRule.ProtectedRoute)
    ) {
      return <ProtectedRoute component={Component} />;
    }

    if (meData && filteredRules.length) {
      const [rule] = filteredRules;
      return <Navigate replace to={getPath(routeMapping, rule)} />;
    }

    return <Component />;
  }
);
